import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Leaf, ArrowLeft, Mail, Lock } from "lucide-react";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 md:px-16 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md w-full mx-auto"
        >
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#78716C] hover:text-[#2C332B] transition-colors mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="flex items-center gap-2 mb-8">
            <Leaf className="w-8 h-8 text-[#7C9A92]" />
            <span className="font-serif text-2xl font-medium text-[#2C332B]">CoachBridge</span>
          </div>

          <h1 className="font-serif text-4xl font-light text-[#2C332B] mb-2">
            Welcome Back
          </h1>
          <p className="text-[#78716C] mb-8">
            Sign in to continue your journey
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#2C332B] font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-14 pl-12 rounded-xl border-stone-200 bg-stone-50/50 focus:ring-2 focus:ring-[#7C9A92]/20 focus:border-[#7C9A92]"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#2C332B] font-medium">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#78716C]" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="h-14 pl-12 rounded-xl border-stone-200 bg-stone-50/50 focus:ring-2 focus:ring-[#7C9A92]/20 focus:border-[#7C9A92]"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <div className="text-right">
              <Link 
                to="/forgot-password?type=user" 
                className="text-sm text-[#7C9A92] hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 rounded-xl bg-[#7C9A92] hover:bg-[#6B8A82] text-white font-medium text-lg transition-all hover:scale-[1.02] active:scale-[0.98]"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <p className="text-center mt-8 text-[#78716C]">
            Don't have an account?{" "}
            <Link to="/register" className="text-[#7C9A92] hover:underline font-medium" data-testid="register-link">
              Create one
            </Link>
          </p>
        </motion.div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <img
          src="https://images.unsplash.com/photo-1505209097149-d3c1c703071a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxzcHJpbmclMjBuYXR1cmUlMjBncm93dGglMjBzcHJvdXQlMjBzZWVkbGluZyUyMHN1bmxpZ2h0fGVufDB8fHx8MTc3MDc1MzQ4Mnww&ixlib=rb-4.1.0&q=85"
          alt="Growth"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#7C9A92]/40 to-[#E09F7D]/30"></div>
        <div className="absolute bottom-12 left-12 right-12">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl">
            <p className="font-serif text-2xl text-[#2C332B] leading-relaxed">
              "Come to me, all you who are weary and burdened, and I will give you rest."
            </p>
            <p className="mt-4 text-[#78716C] font-medium">— Matthew 11:28</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
